import { useCallback, useEffect, useRef, useState } from 'react'
import { CFO_CORE_DUCKDB_DATASET_ID, type AppWorkerClient } from '../data/AppWorkerClient'
import type {
  AiAgentContinueResponse,
  AiAgentEvent,
  AiAgentOutput,
  AiAgentStartRequest,
  AiAgentToolCall,
  AiAgentToolResult,
  AiChatMessage,
  MetricComparisonKind,
  ServerApiClient,
} from '../data/ServerApiClient'
import { makeAnalysisSessionKeyV1 } from './session'

// Reusable, workbook-wide CFO agent client.
//
// This module owns the agent run lifecycle (start -> local tool execution loop
// -> continue), the agent UI run-state, and the implementation of every
// *workbook-generic* tool the agent can call: the canonical period-comparison
// engine (`compare_periods`), the ambient insight spine (`get_insights`), the
// local DuckDB scratch tools (`run_duckdb_sql`/`run_duckdb_plan`/
// `save_agent_result_table`), the browser DuckDB analytic tools (`topk`/
// `groupby`/`correlation`/`outliers`), and `web_search`.
//
// Surface-specific tools (metric tree, statements, GL drill, invoice drivers,
// A/R, etc.) are supplied by the host via `toolHandlers`; host handlers take
// precedence over the built-ins, so a single agent loop powers Growth Analysis
// today and the cfo-home copilot rail next, with no behavior split or
// duplication.

const trimStr = (value: any): string => String(value ?? '').trim()

const perfNow = (): number => (typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now())

const compactJsonSize = (value: any): number => {
  try {
    return JSON.stringify(value ?? null).length
  } catch {
    return -1
  }
}

const summarizeForLog = (value: any, depth = 0): any => {
  if (value == null) return value
  if (typeof value === 'string') return value.length > 500 ? `${value.slice(0, 500)}...` : value
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return {
      type: 'array',
      length: value.length,
      sample: value.slice(0, depth > 1 ? 2 : 5).map((item) => summarizeForLog(item, depth + 1)),
    }
  }
  const out: Record<string, any> = {}
  const entries = Object.entries(value)
  for (const [key, item] of entries.slice(0, depth > 1 ? 12 : 30)) {
    out[key] = summarizeForLog(item, depth + 1)
  }
  if (entries.length > Object.keys(out).length) out.__truncated_keys = entries.length - Object.keys(out).length
  return out
}

const summarizeToolResultForLog = (result: AiAgentToolResult): Record<string, any> => ({
  tool_call_id: result.tool_call_id,
  name: result.name,
  ok: result.ok,
  elapsed_ms: result.elapsed_ms ?? null,
  error: result.error || null,
  result_bytes: compactJsonSize(result.result),
  result_preview: summarizeForLog(result.result),
})

const isNetIncomeBridgeEvidenceRequest = (request: Pick<AiAgentStartRequest, 'workspace_context'>): boolean => {
  const context = request.workspace_context
  if (!context || typeof context !== 'object' || Array.isArray(context)) return false
  const record = context as Record<string, any>
  const sourceComponent = trimStr(record.source_component ?? record.sourceComponent)
  return trimStr(record.interaction) === 'evidence_click' && sourceComponent.startsWith('net_income_bridge')
}

const isInstantCardEnrichmentRequest = (request: Pick<AiAgentStartRequest, 'workspace_context'>): boolean => {
  const context = request.workspace_context
  if (!context || typeof context !== 'object' || Array.isArray(context)) return false
  const record = context as Record<string, any>
  return trimStr(record.interaction) === 'instant_card_enrich' && !!record.instant_card
}

const isInstantCardEnrichmentAllowedToolCall = (toolName: string): boolean => {
  switch (toolName) {
    case 'get_cfo_metric_evidence':
    case 'run_drilldown_recipe':
    case 'compare_periods':
    case 'get_insights':
    case 'search_cfo_gl_summary':
      return true
    default:
      return false
  }
}

const isBridgeAllowedToolCall = (
  toolName: string,
  _request: Pick<AiAgentStartRequest, 'selected_metric_id' | 'workspace_context'>,
): boolean => {
  switch (toolName) {
    case 'list_cfo_page_contracts':
    case 'get_cfo_page_contract':
    case 'get_cfo_metric_evidence':
    case 'get_insights':
    case 'search_cfo_gl_summary':
    case 'compare_periods':
    case 'list_agent_artifacts':
    case 'run_drilldown_recipe':
    case 'get_metric_definition':
      return true
    default:
      return false
  }
}

// Workbook-generic tools implemented directly in the agent bridge's `switch`.
// Surfaced (with any host-registered tool names) when an unimplemented tool is
// requested, so a planner/bridge mismatch fails loudly with the exact gap
// instead of silently zeroing the evidence set.
const BUILTIN_TOOL_NAMES: readonly string[] = [
  'run_duckdb_sql',
  'run_drilldown_recipe',
  'run_duckdb_plan',
  'save_agent_result_table',
  'web_search',
  'compare_periods',
  'get_insights',
  'groupby',
  'topk',
  'correlation',
  'outliers',
]

// Only these stateless, read-only server contracts may execute concurrently. Browser DuckDB,
// scratch writes, and state-dependent drilldowns remain sequential so parallelism cannot change
// financial semantics or session state.
export const CFO_AGENT_PARALLEL_SAFE_TOOL_NAMES: ReadonlySet<string> = new Set([
  'list_cfo_page_contracts',
  'get_cfo_page_contract',
  'get_cfo_metric_evidence',
  'get_insights',
  'search_cfo_gl_summary',
  'compare_periods',
  'get_client_context',
  'investigate_material_variance',
])

export const cfoAgentToolCallBatches = (calls: AiAgentToolCall[]): AiAgentToolCall[][] => {
  const batches: AiAgentToolCall[][] = []
  let parallel: AiAgentToolCall[] = []
  const flush = () => {
    if (parallel.length) batches.push(parallel)
    parallel = []
  }
  for (const call of calls || []) {
    if (CFO_AGENT_PARALLEL_SAFE_TOOL_NAMES.has(call.name)) {
      parallel.push(call)
    } else {
      flush()
      batches.push([call])
    }
  }
  flush()
  return batches
}

const canonicalToolValue = (value: any): any => {
  if (Array.isArray(value)) return value.map(canonicalToolValue)
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, canonicalToolValue(item)]),
  )
}

const toolResultCacheKey = (call: AiAgentToolCall): string =>
  `${call.name}:${JSON.stringify(canonicalToolValue(call.arguments || {}))}`

const COMPARISON_KINDS: ReadonlySet<MetricComparisonKind> = new Set(['mom', 'qoq', 'yoy', 'ytd', 'l3m_vs_prior3m', 'l12m_vs_prior12m'])

const isReadOnlySql = (sql: string): boolean => {
  const trimmed = trimStr(sql)
    .replace(/;+\s*$/g, '')
    .trim()
  if (!trimmed || trimmed.includes(';')) return false
  if (!/^(select|with|describe|show|pragma|explain)\b/i.test(trimmed)) return false
  const lower = ` ${trimmed.toLowerCase()} `
  return ![
    ' attach ',
    ' detach ',
    ' install ',
    ' load ',
    ' copy ',
    ' create ',
    ' drop ',
    ' alter ',
    ' insert ',
    ' update ',
    ' delete ',
    ' truncate ',
    ' vacuum ',
    ' export ',
    ' import ',
    ' read_csv',
    ' read_json',
    ' read_parquet',
    ' glob(',
    ' httpfs',
    ' s3_',
    ' file ',
  ].some((pattern) => lower.includes(pattern))
}

const assertPlanStatement = (sql: string, finalStatement = false): void => {
  const trimmed = trimStr(sql)
    .replace(/;+\s*$/g, '')
    .trim()
  if (!trimmed) throw new Error('Agent DuckDB plan contains an empty SQL statement.')
  if (isReadOnlySql(trimmed)) return
  if (finalStatement) throw new Error('Agent DuckDB final SQL must be read-only.')
  const createMatch = trimmed.match(/^create\s+(?:or\s+replace\s+)?(?:temp|temporary)\s+table\s+([^\s(]+)/i)
  const dropMatch = trimmed.match(/^drop\s+table\s+(?:if\s+exists\s+)?([^\s;]+)/i)
  const tableName = trimStr(
    (createMatch?.[1] || dropMatch?.[1] || '')
      .split('.')
      .pop()
      ?.replace(/^["`]|["`]$/g, ''),
  )
  if (tableName && /^fg_agent_[a-z0-9_]+$/i.test(tableName)) return
  throw new Error('Agent DuckDB scratch writes must use fg_agent_* temp tables.')
}

const toStringArray = (value: any): string[] => {
  if (Array.isArray(value)) return value.map((item) => trimStr(item)).filter(Boolean)
  const single = trimStr(value)
  return single ? [single] : []
}

const clampNumber = (value: any, fallback: number, min: number, max: number): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(max, Math.max(min, parsed))
}

const workerPreviewRowCount = (result: any): number => {
  const direct = Number(result?.preview?.rows ?? result?.rows)
  return Number.isFinite(direct) ? direct : 0
}

const interruptCfoCoreDuckDb = (
  workerClient: AppWorkerClient | null | undefined,
  log: ((event: string, payload?: Record<string, any>) => void) | undefined,
  reason: string,
): void => {
  if (!workerClient) return
  if (typeof workerClient.cache_interrupt_session === 'function') {
    workerClient
      .cache_interrupt_session(CFO_CORE_DUCKDB_DATASET_ID, { timeoutMs: 5000 })
      .then((result) => {
        log?.('agent_duckdb_interrupt_done', {
          dataset_id: CFO_CORE_DUCKDB_DATASET_ID,
          reason,
          interrupted: Boolean(result?.interrupted),
        })
      })
      .catch((error) => {
        log?.('agent_duckdb_interrupt_failed', {
          dataset_id: CFO_CORE_DUCKDB_DATASET_ID,
          reason,
          error: trimStr(error?.message || error),
        })
      })
  }
}

/** Base request shared by start + continue, minus the per-turn question. */
export type CfoAgentBaseRequest = Omit<AiAgentStartRequest, 'question'>

export type CfoAgentToolHandler = (args: Record<string, any>, call: AiAgentToolCall) => Promise<any>

export type CfoAgentToolHandlers = Record<string, CfoAgentToolHandler>

/**
 * Resolves a browser-DuckDB worker `session_key` for the analytic tools
 * (`topk`/`groupby`/`correlation`/`outliers`). Hosts that already materialized
 * a session over the active dataset can return its key here; otherwise callers
 * pass `session_key`/`sql`/`parquet_uri` in the tool args.
 */
export type CfoAgentSessionResolver = (
  args: Record<string, any>,
  call: AiAgentToolCall,
) => Promise<{ session_key: string } | null | undefined>

export type UseCfoAgentConfig = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId?: string | null
  /** Surface-specific tool handlers; merged OVER the built-in workbook-generic tools. */
  toolHandlers?: CfoAgentToolHandlers
  /** Optional worker-session resolver for the analytic tools. */
  resolveAnalysisSession?: CfoAgentSessionResolver
  /** Structured client logger (e.g. cfoAgentLog). */
  log?: (event: string, payload?: Record<string, any>) => void
}

export type CfoAgentSendOptions = {
  /** Invoked once the final structured answer is available. */
  onAnswer?: (response: AiAgentContinueResponse) => void
  /** Lets a host surface append deterministic progress cards as local evidence arrives. */
  onToolResult?: (
    result: AiAgentToolResult,
    context: {
      call: AiAgentToolCall
      agent_run_id: string
      tool_results: AiAgentToolResult[]
    },
  ) => AiChatMessage[] | void
}

export type UseCfoAgentResult = {
  messages: AiChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<AiChatMessage[]>>
  events: AiAgentEvent[]
  outputs: AiAgentOutput[]
  response: AiAgentContinueResponse | null
  traceOpen: boolean
  setTraceOpen: React.Dispatch<React.SetStateAction<boolean>>
  /** Execute one agent turn: start -> tool loop -> continue. Throws on failure. */
  sendMessage: (text: string, baseRequest: CfoAgentBaseRequest, opts?: CfoAgentSendOptions) => Promise<AiAgentContinueResponse>
  /** Execute a single tool call (host handlers first, then built-ins). */
  executeTool: (call: AiAgentToolCall) => Promise<AiAgentToolResult>
  reset: () => void
}

export function useCfoAgent(config: UseCfoAgentConfig): UseCfoAgentResult {
  const { appApi, workerClient, log } = config

  const [messages, setMessages] = useState<AiChatMessage[]>([])
  const messagesRef = useRef<AiChatMessage[]>([])
  const [events, setEvents] = useState<AiAgentEvent[]>([])
  const [outputs, setOutputs] = useState<AiAgentOutput[]>([])
  const [response, setResponse] = useState<AiAgentContinueResponse | null>(null)
  const [traceOpen, setTraceOpen] = useState(false)

  // Latest-value refs so the executor identity stays stable while always
  // reading the freshest host handlers / config.
  const toolHandlersRef = useRef<CfoAgentToolHandlers | undefined>(config.toolHandlers)
  toolHandlersRef.current = config.toolHandlers
  const resolveSessionRef = useRef<CfoAgentSessionResolver | undefined>(config.resolveAnalysisSession)
  resolveSessionRef.current = config.resolveAnalysisSession
  const workbookIdRef = useRef<string | null | undefined>(config.workbookId)
  workbookIdRef.current = config.workbookId
  const appApiRef = useRef(appApi)
  appApiRef.current = appApi
  const workerClientRef = useRef<AppWorkerClient | null | undefined>(workerClient)
  workerClientRef.current = workerClient
  const logRef = useRef<typeof log>(log)
  logRef.current = log
  const mountedRef = useRef(true)
  const activeRunIdRef = useRef<string | null>(null)
  const runSequenceRef = useRef(0)
  // Cache materialized worker sessions by source so repeated analytic tool
  // calls in a run reuse the same tidy table instead of re-materializing.
  const sessionCacheRef = useRef<Map<string, string>>(new Map())

  const cancelRunSideEffects = useCallback((runId: string | null, reason: string): void => {
    sessionCacheRef.current.clear()
    interruptCfoCoreDuckDb(workerClientRef.current, logRef.current, reason)
    const currentAppApi = appApiRef.current
    if (runId && typeof currentAppApi.cancelAiAgentRun === 'function') {
      currentAppApi.cancelAiAgentRun({ agent_run_id: runId }).catch(() => {})
    }
  }, [])

  const resolveWorkerSession = useCallback(
    async (args: Record<string, any>, call: AiAgentToolCall): Promise<string> => {
      const explicit = trimStr(args.session_key ?? args.sessionKey)
      if (explicit) return explicit

      const resolver = resolveSessionRef.current
      if (resolver) {
        const resolved = await resolver(args, call)
        const key = trimStr(resolved?.session_key)
        if (key) return key
      }

      const sql = trimStr(args.sql ?? args.source_sql ?? args.sourceSql)
      const parquetUri = trimStr(args.parquet_uri ?? args.parquetUri)
      if (sql || parquetUri) {
        if (!workerClient || typeof workerClient.ai_materialize_session !== 'function') {
          throw new Error('Local DuckDB worker is not available to materialize an analysis session.')
        }
        if (sql && !isReadOnlySql(sql)) {
          throw new Error('Analysis session sql must be a read-only query (SELECT/WITH/...).')
        }
        const cacheKey = sql ? `sql:${sql}` : `parquet:${parquetUri}`
        const cached = sessionCacheRef.current.get(cacheKey)
        if (cached) return cached
        const sessionKey = makeAnalysisSessionKeyV1()
        const mat = await workerClient.ai_materialize_session({
          session_key: sessionKey,
          source: sql ? { kind: 'worksheet_sql', sql } : { kind: 'parquet_uri', parquet_uri: parquetUri },
          options: { max_base_rows: 50000, drop_if_exists: true },
        })
        const resolved = trimStr(mat?.session?.session_key) || sessionKey
        sessionCacheRef.current.set(cacheKey, resolved)
        return resolved
      }

      throw new Error(
        'No analysis session is available for this tool. Provide session_key, sql, or parquet_uri (or wire resolveAnalysisSession).',
      )
    },
    [workerClient],
  )

  const executeTool = useCallback(
    async (call: AiAgentToolCall): Promise<AiAgentToolResult> => {
      const startedAt = perfNow()
      const args = call.arguments && typeof call.arguments === 'object' ? call.arguments : {}
      const finish = (ok: boolean, payload: { result?: any; error?: string | null }): AiAgentToolResult => ({
        tool_call_id: call.id,
        name: call.name,
        ok,
        result: payload.result,
        error: payload.error || null,
        elapsed_ms: Math.round(perfNow() - startedAt),
      })

      try {
        // Host (surface-specific) handlers take precedence over built-ins.
        const handler = toolHandlersRef.current?.[call.name]
        if (handler) {
          return finish(true, { result: await handler(args, call) })
        }

        switch (call.name) {
          case 'run_duckdb_sql': {
            if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
              throw new Error('Local DuckDB worker is not available for this agent run.')
            }
            const sql = trimStr(args.sql)
            if (!sql) throw new Error('run_duckdb_sql requires a sql argument.')
            if (!isReadOnlySql(sql)) {
              throw new Error('run_duckdb_sql is read-only. Use save_agent_result_table for fg_agent_* scratch outputs.')
            }
            const result = await workerClient.duckdb_run_sql(sql, {
              previewRowLimit: clampNumber(args.preview_row_limit ?? args.previewRowLimit, 1000, 1, 5000),
              timeoutMs: clampNumber(args.timeout_ms ?? args.timeoutMs, 60000, 1000, 120000),
              cacheExtracts: true,
              allowMutating: false,
              format: 'preview',
              returnIpc: false,
              caller: 'FuseGridAgent:run_duckdb_sql',
            })
            return finish(true, {
              result: {
                ...result,
                summary: `DuckDB returned ${Number(result?.rows || 0).toLocaleString()} row(s).`,
                query_purpose: trimStr(args.query_purpose ?? args.queryPurpose) || null,
                analysis_intent: trimStr(args.analysis_intent ?? args.analysisIntent) || null,
                sql,
              },
            })
          }
          case 'run_duckdb_plan': {
            if (!workerClient || typeof workerClient.duckdb_run_plan !== 'function') {
              throw new Error('Local DuckDB worker is not available for this agent run.')
            }
            const plan = args.plan && typeof args.plan === 'object' ? args.plan : args
            const statements = Array.isArray(plan?.statements) ? plan.statements : []
            for (const statement of statements) assertPlanStatement(String(statement || ''))
            assertPlanStatement(String(plan?.finalSql || ''), true)
            for (const statement of Array.isArray(plan?.cleanup) ? plan.cleanup : []) {
              assertPlanStatement(String(statement || ''))
            }
            const result = await workerClient.duckdb_run_plan(plan, {
              previewRowLimit: clampNumber(args.preview_row_limit ?? args.previewRowLimit, 1000, 1, 5000),
              timeoutMs: clampNumber(args.timeout_ms ?? args.timeoutMs, 60000, 1000, 120000),
              caller: 'FuseGridAgent:run_duckdb_plan',
            })
            return finish(true, {
              result: {
                summary: `DuckDB plan returned ${Number(result?.rows || 0).toLocaleString()} row(s).`,
                ...result,
              },
            })
          }
          case 'save_agent_result_table': {
            if (!workerClient || typeof workerClient.duckdb_create_temp_table_from_rows !== 'function') {
              throw new Error('Local DuckDB worker is not available for result-table writes.')
            }
            const tableName = trimStr(args.table_name ?? args.tableName)
            if (!/^fg_agent_[a-z0-9_]+$/i.test(tableName)) {
              throw new Error('Agent scratch tables must be namespaced as fg_agent_*.')
            }
            const columns = Array.isArray(args.columns) ? args.columns : []
            const rows = Array.isArray(args.rows) ? args.rows : []
            const result = await workerClient.duckdb_create_temp_table_from_rows(tableName, columns, rows, {
              timeoutMs: 120000,
              dropIfExists: true,
            })
            return finish(true, {
              result: {
                summary: `Saved ${Number(result?.rows_inserted || 0).toLocaleString()} row(s) to ${tableName}.`,
                ...result,
              },
            })
          }
          case 'web_search': {
            return finish(true, {
              result: {
                summary: 'Web search is enabled for the model response through the server AI provider.',
                query: args.query || '',
              },
            })
          }
          case 'compare_periods': {
            return finish(true, {
              result: await runComparePeriods(appApi, workbookIdRef.current, args),
            })
          }
          case 'get_insights': {
            return finish(true, {
              result: await runGetInsights(appApi, workbookIdRef.current, args),
            })
          }
          case 'groupby': {
            if (!workerClient || typeof workerClient.ai_tool_groupby !== 'function') {
              throw new Error('Local DuckDB worker is not available for the groupby tool.')
            }
            const sessionKey = await resolveWorkerSession(args, call)
            const groupBy = toStringArray(args.group_by ?? args.groupBy ?? args.dimensions)
            if (!groupBy.length) throw new Error('groupby requires at least one group_by field.')
            const result = await workerClient.ai_tool_groupby({
              session_key: sessionKey,
              group_by: groupBy,
              measure: trimStr(args.measure) || null,
              agg: trimStr(args.agg) || null,
              limit: Number.isFinite(Number(args.limit)) ? Number(args.limit) : null,
              preview_row_limit: clampNumber(args.preview_row_limit ?? args.previewRowLimit, 200, 1, 5000),
            })
            return finish(true, {
              result: {
                summary: `${trimStr(result?.title) || 'groupby'} returned ${workerPreviewRowCount(result).toLocaleString()} row(s).`,
                ...result,
              },
            })
          }
          case 'topk': {
            if (!workerClient || typeof workerClient.ai_tool_topk !== 'function') {
              throw new Error('Local DuckDB worker is not available for the topk tool.')
            }
            const sessionKey = await resolveWorkerSession(args, call)
            const columns = toStringArray(args.columns ?? args.group_by ?? args.dimensions)
            const orderBy = trimStr(args.order_by ?? args.orderBy ?? args.measure)
            if (!columns.length) throw new Error('topk requires at least one column.')
            if (!orderBy) throw new Error('topk requires an order_by field.')
            const result = await workerClient.ai_tool_topk({
              session_key: sessionKey,
              columns,
              order_by: orderBy,
              order_dir: trimStr(args.order_dir ?? args.orderDir) || null,
              where: args.where && typeof args.where === 'object' ? args.where : null,
              limit: Number.isFinite(Number(args.limit)) ? Number(args.limit) : null,
              preview_row_limit: clampNumber(args.preview_row_limit ?? args.previewRowLimit, 200, 1, 5000),
            })
            return finish(true, {
              result: {
                summary: `${trimStr(result?.title) || 'topk'} returned ${workerPreviewRowCount(result).toLocaleString()} row(s).`,
                ...result,
              },
            })
          }
          case 'correlation': {
            if (!workerClient || typeof workerClient.ai_tool_correlation !== 'function') {
              throw new Error('Local DuckDB worker is not available for the correlation tool.')
            }
            const sessionKey = await resolveWorkerSession(args, call)
            const x = trimStr(args.x ?? args.x_field ?? args.xField)
            const y = trimStr(args.y ?? args.y_field ?? args.yField)
            if (!x || !y) throw new Error('correlation requires x and y fields.')
            const result = await workerClient.ai_tool_correlation({
              session_key: sessionKey,
              x,
              y,
              preview_row_limit: clampNumber(args.preview_row_limit ?? args.previewRowLimit, 200, 1, 5000),
            })
            return finish(true, {
              result: {
                summary: `${trimStr(result?.title) || `correlation(${x}, ${y})`} computed.`,
                ...result,
              },
            })
          }
          case 'outliers': {
            if (!workerClient || typeof workerClient.ai_tool_explain_outliers !== 'function') {
              throw new Error('Local DuckDB worker is not available for the outliers tool.')
            }
            const sessionKey = await resolveWorkerSession(args, call)
            const measure = trimStr(args.measure ?? args.metric ?? args.value)
            if (!measure) throw new Error('outliers requires a measure field.')
            const result = await workerClient.ai_tool_explain_outliers({
              session_key: sessionKey,
              measure,
              by: trimStr(args.by ?? args.group_by ?? args.dimension) || null,
              limit: Number.isFinite(Number(args.limit)) ? Number(args.limit) : null,
              preview_row_limit: clampNumber(args.preview_row_limit ?? args.previewRowLimit, 200, 1, 5000),
            })
            return finish(true, {
              result: {
                summary: `${trimStr(result?.title) || `outliers(${measure})`} returned ${workerPreviewRowCount(result).toLocaleString()} row(s).`,
                ...result,
              },
            })
          }
          default: {
            const hostTools = Object.keys(toolHandlersRef.current || {})
            const available = [...hostTools, ...BUILTIN_TOOL_NAMES]
            return finish(false, {
              result: {
                summary: `Unimplemented tool "${call.name}": no host handler or built-in bridge implementation. The surface that mounted this agent must register a handler for it.`,
                unimplemented_tool: call.name,
                available_tools: available,
              },
              error: `Tool ${call.name} is not implemented in the CFO agent bridge (available: ${available.join(', ') || 'none'}).`,
            })
          }
        }
      } catch (err: any) {
        return finish(false, {
          error: trimStr(err?.message || err) || `Tool ${call.name} failed.`,
        })
      }
    },
    [appApi, resolveWorkerSession, workerClient],
  )

  const executeToolRef = useRef(executeTool)
  executeToolRef.current = executeTool

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      runSequenceRef.current += 1
      const runId = activeRunIdRef.current
      activeRunIdRef.current = null
      cancelRunSideEffects(runId, 'surface_unmount')
    }
  }, [cancelRunSideEffects])

  const reset = useCallback(() => {
    runSequenceRef.current += 1
    const runId = activeRunIdRef.current
    activeRunIdRef.current = null
    messagesRef.current = []
    setMessages([])
    setEvents([])
    setOutputs([])
    setResponse(null)
    setTraceOpen(false)
    cancelRunSideEffects(runId, 'agent_reset')
  }, [cancelRunSideEffects])

  const sendMessage = useCallback(
    async (text: string, baseRequest: CfoAgentBaseRequest, opts?: CfoAgentSendOptions): Promise<AiAgentContinueResponse> => {
      const content = trimStr(text)
      if (!content) throw new Error('Message cannot be empty.')

      let startedRunId: string | null = null
      const previousRunId = activeRunIdRef.current
      const runSequence = runSequenceRef.current + 1
      runSequenceRef.current = runSequence
      activeRunIdRef.current = null
      if (previousRunId) {
        cancelRunSideEffects(previousRunId, 'new_agent_run')
      }
      try {
        setEvents([])
        setOutputs([])
        setResponse(null)
        setTraceOpen(false)
        const nextMessages: AiChatMessage[] = [...messagesRef.current, { role: 'user', content }]
        messagesRef.current = nextMessages
        setMessages(nextMessages)

        const runStartedAt = perfNow()
        logRef.current?.('agent_run_start', {
          workbook_id: baseRequest.workbook_id ?? null,
          connection_id: baseRequest.connection_id ?? null,
          active_section: baseRequest.active_section ?? null,
          selected_metric_id: baseRequest.selected_metric_id ?? null,
          model_id: baseRequest.model_id ?? null,
          provider: baseRequest.provider ?? null,
          max_tokens: baseRequest.max_tokens ?? null,
          reasoning_effort: baseRequest.reasoning_effort ?? null,
          question_chars: content.length,
          question_preview: content.length > 500 ? `${content.slice(0, 500)}...` : content,
          workspace_context_bytes: compactJsonSize(baseRequest.workspace_context),
          workspace_context_preview: summarizeForLog(baseRequest.workspace_context),
        })
        const startRequestAt = perfNow()
        const currentAppApi = appApiRef.current
        const started = await currentAppApi.startAiAgentRun({
          ...baseRequest,
          question: content,
        })
        startedRunId = started.agent_run_id
        if (!mountedRef.current || runSequenceRef.current !== runSequence) {
          appApiRef.current.cancelAiAgentRun({ agent_run_id: started.agent_run_id }).catch(() => {})
          throw new Error('Agent run cancelled because the surface unmounted or a newer run started.')
        }
        activeRunIdRef.current = started.agent_run_id
        logRef.current?.('agent_start_request_done', {
          agent_run_id: started.agent_run_id,
          elapsed_ms: Math.round(perfNow() - startRequestAt),
          tool_count: started.tool_calls?.length || 0,
          status: started.status,
          events: (started.events || []).map((event) => ({
            kind: event.kind,
            message: event.message,
            data: summarizeForLog(event.data),
          })),
          tool_calls: (started.tool_calls || []).map((call) => ({
            id: call.id,
            name: call.name,
            arguments: summarizeForLog(call.arguments),
            argument_bytes: compactJsonSize(call.arguments),
          })),
        })
        setEvents(started.events || [])

        const assertRunCurrent = (): void => {
          if (!mountedRef.current || runSequenceRef.current !== runSequence || activeRunIdRef.current !== started.agent_run_id) {
            throw new Error('Agent run cancelled because the surface unmounted or a newer run started.')
          }
        }

        const toolResults: AiAgentToolResult[] = []
        const toolResultOrder = new Map<string, number>()
        const toolResultCache = new Map<string, Promise<AiAgentToolResult>>()
        let nextToolResultOrder = 0
        const executeRunTool = async (call: AiAgentToolCall): Promise<AiAgentToolResult> => {
          if (!CFO_AGENT_PARALLEL_SAFE_TOOL_NAMES.has(call.name)) return await executeToolRef.current(call)
          const key = toolResultCacheKey(call)
          const cached = toolResultCache.get(key)
          if (cached) {
            const result = await cached
            return {
              ...result,
              tool_call_id: call.id,
              elapsed_ms: 0,
              result: {
                ...(result.result && typeof result.result === 'object' ? result.result : { value: result.result }),
                cache_state: 'agent_run_hit',
              },
            }
          }
          const execution = executeToolRef.current(call)
          toolResultCache.set(key, execution)
          return await execution
        }
        const runToolCalls = async (calls: AiAgentToolCall[], phase: string): Promise<void> => {
          for (const call of calls || []) {
            if (!toolResultOrder.has(call.id)) {
              toolResultOrder.set(call.id, nextToolResultOrder)
              nextToolResultOrder += 1
            }
          }
          const batches = cfoAgentToolCallBatches(calls || [])
          if (batches.length > 1 || batches.some((batch) => batch.length > 1)) {
            for (const batch of batches) {
              if (batch.length > 1) {
                await Promise.all(batch.map((call) => runToolCalls([call], phase)))
              } else {
                await runToolCalls(batch, phase)
              }
            }
            return
          }
          for (const call of calls || []) {
            assertRunCurrent()
            if (isNetIncomeBridgeEvidenceRequest(baseRequest) && !isBridgeAllowedToolCall(call.name, baseRequest)) {
              logRef.current?.('tool_skipped', {
                agent_run_id: started.agent_run_id,
                tool_call_id: call.id,
                tool_name: call.name,
                phase,
                reason: 'bridge_evidence_first_layer_only',
              })
              setEvents((current) => [
                ...current,
                {
                  id: `${call.id}_skipped`,
                  kind: 'tool_skipped',
                  message: `${call.name} skipped for first-layer chart evidence.`,
                  data: { tool_call: call },
                },
              ])
              continue
            }
            if (isInstantCardEnrichmentRequest(baseRequest) && !isInstantCardEnrichmentAllowedToolCall(call.name)) {
              logRef.current?.('tool_skipped', {
                agent_run_id: started.agent_run_id,
                tool_call_id: call.id,
                tool_name: call.name,
                phase,
                reason: 'instant_card_enrich_fast_lane_only',
              })
              setEvents((current) => [
                ...current,
                {
                  id: `${call.id}_skipped`,
                  kind: 'tool_skipped',
                  message: `${call.name} skipped for instant-card enrichment.`,
                  data: { tool_call: call },
                },
              ])
              continue
            }
            logRef.current?.('tool_running', {
              agent_run_id: started.agent_run_id,
              tool_call_id: call.id,
              tool_name: call.name,
              phase,
              arguments: summarizeForLog(call.arguments),
              argument_bytes: compactJsonSize(call.arguments),
            })
            setEvents((current) => [
              ...current,
              {
                id: `${call.id}_running`,
                kind: 'tool_running',
                message: `Running ${call.name}.`,
                data: { tool_call: call },
              },
            ])
            const result = await executeRunTool(call)
            assertRunCurrent()
            toolResults.push(result)
            toolResults.sort(
              (left, right) =>
                (toolResultOrder.get(left.tool_call_id) ?? Number.MAX_SAFE_INTEGER) -
                (toolResultOrder.get(right.tool_call_id) ?? Number.MAX_SAFE_INTEGER),
            )
            logRef.current?.('tool_result', {
              agent_run_id: started.agent_run_id,
              tool_name: call.name,
              phase,
              ok: result.ok,
              elapsed_ms: result.elapsed_ms ?? null,
              error: result.error || null,
              result_bytes: compactJsonSize(result.result),
              result_preview: summarizeForLog(result.result),
            })
            if (call.name === 'run_drilldown_recipe' && result.result && typeof result.result === 'object') {
              const payload = result.result as Record<string, any>
              logRef.current?.('cfo_drilldown_recipe_result', {
                agent_run_id: started.agent_run_id,
                tool_call_id: call.id,
                selected_metric_id: trimStr(payload.metric_id ?? call.arguments?.metric_id ?? baseRequest.selected_metric_id) || null,
                source_component: trimStr(call.arguments?.source_component ?? baseRequest.workspace_context?.source_component) || null,
                selected_recipe_id: trimStr(payload.recipe_id ?? call.arguments?.recipe_id) || null,
                manifest_version: trimStr(payload.manifest_version) || null,
                readiness_status: trimStr(payload.status) || null,
                missing_views: Array.isArray(payload.missing_tables) ? payload.missing_tables : [],
                fallback_used: Boolean(payload.telemetry?.fallback_used),
                row_count:
                  Number(payload.telemetry?.row_count) ||
                  (Array.isArray(payload.category_rows)
                    ? payload.category_rows.length
                    : Array.isArray(payload.driver_rows)
                      ? payload.driver_rows.length
                      : Array.isArray(payload.customer_rows)
                        ? payload.customer_rows.length
                        : Array.isArray(payload.transaction_rows)
                          ? payload.transaction_rows.length
                          : null),
                render_kind: trimStr(payload.view_kind) || null,
              })
            }
            setEvents((current) => [
              ...current,
              {
                id: `${call.id}_result`,
                kind: result.ok ? 'tool_result' : 'tool_error',
                message: result.ok
                  ? `${call.name} finished in ${result.elapsed_ms ?? 0}ms.`
                  : `${call.name} failed: ${trimStr(result.error || '') || 'No error detail.'}`,
                data: { result },
              },
            ])
            const progressMessages = opts?.onToolResult?.(result, {
              call,
              agent_run_id: started.agent_run_id,
              tool_results: [...toolResults],
            })
            if (Array.isArray(progressMessages) && progressMessages.length) {
              const mergedMessages: AiChatMessage[] = [...messagesRef.current, ...progressMessages]
              messagesRef.current = mergedMessages
              setMessages(mergedMessages)
              logRef.current?.('progress_messages_rendered', {
                agent_run_id: started.agent_run_id,
                tool_name: call.name,
                message_count: progressMessages.length,
                kinds: progressMessages.map((message) => message.metadata?.kind || 'text'),
              })
            }
          }
        }

        await runToolCalls(started.tool_calls || [], 'initial')

        let resp: AiAgentContinueResponse | null = null
        for (let round = 0; round < 3; round += 1) {
          assertRunCurrent()
          const continueRequestAt = perfNow()
          logRef.current?.('agent_continue_request_start', {
            agent_run_id: started.agent_run_id,
            round,
            tool_result_count: toolResults.length,
            tool_result_bytes: compactJsonSize(toolResults),
            tool_results: toolResults.map(summarizeToolResultForLog),
          })
          resp = await appApiRef.current.continueAiAgentRun({
            ...baseRequest,
            question: content,
            agent_run_id: started.agent_run_id,
            financial_work_run_id: started.financial_work_run_id || null,
            tool_results: toolResults,
          })
          assertRunCurrent()
          logRef.current?.('agent_continue_request_done', {
            agent_run_id: started.agent_run_id,
            round,
            elapsed_ms: Math.round(perfNow() - continueRequestAt),
            status: resp.status,
            planned_tool_count: resp.tool_calls?.length || 0,
            planned_tool_calls: (resp.tool_calls || []).map((call) => ({
              id: call.id,
              name: call.name,
              arguments: summarizeForLog(call.arguments),
              argument_bytes: compactJsonSize(call.arguments),
            })),
            model_elapsed_ms: resp.timings?.model_elapsed_ms ?? null,
            timings: resp.timings || null,
            input_tokens: resp.usage?.input_tokens ?? null,
            output_tokens: resp.usage?.output_tokens ?? null,
            total_tokens: resp.usage?.total_tokens ?? null,
            estimated_cost_usd: resp.usage?.estimated_cost_usd ?? null,
            telemetry_status: resp.telemetry?.status || null,
            telemetry_warnings: resp.telemetry_warnings?.length || 0,
            answer: summarizeForLog(resp.answer),
            gap_analysis: summarizeForLog(resp.gap_analysis),
            followups: summarizeForLog(resp.followups),
            outputs: (resp.outputs || []).map((output) => ({
              id: output.id,
              kind: output.kind,
              title: output.title,
              rows: Array.isArray(output.rows) ? output.rows.length : null,
              bytes: compactJsonSize(output),
            })),
            visuals: (resp.visuals || []).map((visual) => ({
              id: visual.id,
              kind: visual.kind,
              title: visual.title,
              bytes: compactJsonSize(visual),
            })),
          })
          setEvents((current) => [...current, ...(resp?.events || [])])
          if (resp.status === 'needs_tool_results' && (resp.tool_calls || []).length) {
            await runToolCalls(resp.tool_calls || [], `continue_${round + 1}`)
            continue
          }
          break
        }
        if (!resp) throw new Error('Agent did not return a response.')
        if (resp.status === 'needs_tool_results') {
          throw new Error('Agent requested more tool results than the local loop limit allows.')
        }
        setOutputs(resp.outputs || [])
        setResponse(resp)

        const assistantText = trimStr(resp.answer?.headline) || 'Analysis canvas prepared.'
        const finalMessages: AiChatMessage[] = [
          ...messagesRef.current,
          {
            role: 'assistant',
            content: assistantText,
            metadata: { kind: 'final_headline' },
          },
        ]
        messagesRef.current = finalMessages
        setMessages(finalMessages)

        opts?.onAnswer?.(resp)
        logRef.current?.('answer_rendered', {
          agent_run_id: started.agent_run_id,
          elapsed_ms: Math.round(perfNow() - runStartedAt),
          visual_count: resp.visuals?.length || 0,
          output_count: resp.outputs?.length || 0,
          followup_count: resp.followups?.length || 0,
          headline: resp.answer?.headline || null,
          executive_summary_chars: trimStr(resp.answer?.executive_summary).length,
          detailed_narrative_count: resp.answer?.detailed_narrative?.length || 0,
          caveat_count: resp.answer?.caveats?.length || 0,
        })
        return resp
      } finally {
        if (startedRunId && runSequenceRef.current === runSequence && activeRunIdRef.current === startedRunId) {
          activeRunIdRef.current = null
        }
      }
    },
    [cancelRunSideEffects],
  )

  return {
    messages,
    setMessages,
    events,
    outputs,
    response,
    traceOpen,
    setTraceOpen,
    sendMessage,
    executeTool,
    reset,
  }
}

/**
 * Route the agent `compare_periods` tool to the canonical period-comparison
 * engine (`getQuickBooksMetricComparisons`) so the agent shares the same
 * MoM/QoQ/YoY/L3M/L12M definitions ($ and % of sales) as the brief, ratios,
 * and presentation surfaces. The deterministic planner passes a surface metric
 * id placeholder (`selected`) that does not map to the registry, so it is only
 * used as a metric filter when an explicit `metrics` array is supplied.
 */
// Placeholder/colloquial metric tokens the planner may emit, normalized so the
// comparison engine never receives a filter that resolves to zero metrics.
const COMPARISON_METRIC_PLACEHOLDERS: ReadonlySet<string> = new Set(['selected', 'none', 'null', 'undefined', 'all', ''])
const COMPARISON_METRIC_ALIASES: Record<string, string> = {
  profit: 'net_income',
  profits: 'net_income',
  income: 'net_income',
  earnings: 'net_income',
  'net profit': 'net_income',
  margin: 'gross_margin',
  'gross margin': 'gross_margin',
  sales: 'revenue',
  topline: 'revenue',
  'top line': 'revenue',
  opex: 'operating_expenses',
  cost: 'cost_of_sales',
  costs: 'cost_of_sales',
  cogs: 'cost_of_sales',
  cost_of_goods_sold: 'cost_of_sales',
  'cost of goods sold': 'cost_of_sales',
  'cost of sales': 'cost_of_sales',
}

function normalizeComparisonMetricIds(raw: string[]): string[] {
  const out: string[] = []
  for (const token of raw) {
    const lowered = token.toLowerCase().trim()
    if (COMPARISON_METRIC_PLACEHOLDERS.has(lowered)) continue
    const resolved = COMPARISON_METRIC_ALIASES[lowered] ?? lowered
    if (resolved && !out.includes(resolved)) out.push(resolved)
  }
  return out
}

async function runComparePeriods(appApi: ServerApiClient, workbookId: string | null | undefined, args: Record<string, any>): Promise<any> {
  const wb = trimStr(workbookId)
  if (!wb) throw new Error('compare_periods requires an active workbook id.')

  // The deterministic server planner passes a placeholder metric id ("selected")
  // when no specific metric is in focus (ai_agent.rs). That token is not a real
  // registry id, so forwarding it as a metrics filter makes the comparison
  // engine resolve ZERO definitions and return 0 metric rows ("compare_periods
  // returned 0 metric(s)"). Strip placeholders and apply the same light aliases
  // the other CFO tools use; if nothing real remains, omit the filter so the
  // engine returns the full canonical registry instead of an empty set.
  const requestedMetrics = normalizeComparisonMetricIds(
    toStringArray(args.metrics ?? args.metric_ids ?? args.metricIds ?? args.metric_id ?? args.metricId ?? args.metric),
  )
  const requestedKinds = toStringArray(args.comparisons ?? args.kinds ?? args.kind).filter((kind): kind is MetricComparisonKind =>
    COMPARISON_KINDS.has(kind as MetricComparisonKind),
  ) as MetricComparisonKind[]
  const asOf = trimStr(args.as_of ?? args.asOf) || null

  const resp = await appApi.getQuickBooksMetricComparisons(wb, {
    asOf,
    comparisons: requestedKinds.length ? requestedKinds : null,
    metrics: requestedMetrics.length ? requestedMetrics : null,
  })

  const summary = `Period comparisons for ${resp.metrics.length} metric(s) as of ${resp.as_of} (${resp.comparisons.join(', ')}).`
  return { summary, ...resp }
}

/**
 * Route the agent `get_insights` tool to the CFO ambient insight spine
 * (`getQuickBooksInsights`): ranked, deterministic "what changed / what needs
 * attention" cards with drill targets, computed server-side over the canonical
 * comparison engine.
 */
async function runGetInsights(appApi: ServerApiClient, workbookId: string | null | undefined, args: Record<string, any>): Promise<any> {
  const wb = trimStr(workbookId)
  if (!wb) throw new Error('get_insights requires an active workbook id.')

  const asOf = trimStr(args.as_of ?? args.asOf) || null
  const resp = await appApi.getQuickBooksInsights(wb, { asOf })
  const summary = `${resp.attention_count} insight card(s) need attention of ${resp.cards.length} total as of ${resp.as_of}.`
  return { summary, ...resp }
}
